import { billetStatus } from "@/shared/selectors/BilletStatusSelector";
import {
	eventTypes,
	productionTypes,
} from "@/shared/selectors/EventTypeSelector";
import {
	dxtToLocalServerTime,
	getMomentTz,
	TimeService,
} from "@dexteel/mesf-core";
import { Done, DoneAll } from "@mui/icons-material";
import {
	Box,
	Button,
	Checkbox,
	FormControlLabel,
	Tooltip,
	Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useEventDragDrop } from "../hooks/useEventDragDrop";
import { useEventHandlers } from "../hooks/useEventHandlers";
import { Event, EventSegment } from "../models/Event";
import {
	addDays,
	calculateNewEventDates,
	compareEventSegments,
	countSectionsByStatus,
	getDayKey,
	isDateInDragRange,
	isDayInCurrentMonth,
	isToday,
	organizeEventsInRows,
	startOfMonth,
	startOfWeek,
} from "../utils/calendarUtils";
import { DragModeSnackbar } from "./DragModeSnackbar";
import { ConfirmMoveEventModal } from "./modals/ConfirmMoveEventModal";
import { MoveEventData, ProcessedRow } from "../models/utils";

const moment = getMomentTz();

const getNarrowBorderLeft = (borderLeft?: string | number) => {
	if (typeof borderLeft !== "string") return borderLeft;

	const width = Number.parseInt(borderLeft, 10);
	if (!width || width <= 10) return borderLeft;

	return borderLeft.replace(/^\d+px/, `${10}px`);
};

const formatTimeRange = (startDate: Date, endDate: Date): string => {
	const serverTz = TimeService.getInstance().getServerTimeZone();
	const startMoment = moment.tz(startDate, serverTz);
	const endMoment = moment.tz(endDate, serverTz);
	return `${startMoment.format("MM/DD HH:mm")} - ${endMoment.format(
		"MM/DD HH:mm",
	)}`;
};

interface EnhancedMonthCalendarProps {
	events: Event[];
	initialDate?: Date;
	onEventClick: (event: Event | null) => void;
	onDateRangeSelect?: (startDate: Date, endDate: Date) => void;
	onEventDrop?: (
		eventId: number,
		newStartDate: Date,
		newEndDate: Date,
	) => Promise<void> | void;
	weekStartsOn?: 0 | 1;
	editable?: boolean;
	selectable?: boolean;
	eventsLimitPerDay?: number;
	eventLimitText?: string;
	showContextMenu?: Function;
}

export const EnhancedMonthCalendar = ({
	events = [],
	initialDate = new Date(),
	onEventClick,
	onDateRangeSelect,
	onEventDrop,
	weekStartsOn = 0,
	editable = false,
	selectable = false,
	eventsLimitPerDay = 3,
	eventLimitText = "+{count} more",
	showContextMenu = () => {},
}: EnhancedMonthCalendarProps) => {
	const [currentDate, setCurrentDate] = useState(initialDate);
	const [expandedDays, setExpandedDays] = useState<Set<string>>(new Set());
	const [dragStartDate, setDragStartDate] = useState<Date | null>(null);
	const [dragEndDate, setDragEndDate] = useState<Date | null>(null);
	const [isDragging, setIsDragging] = useState(false);
	const [hoveredBilletStatusCode, setHoveredBilletStatusCode] = useState<
		string | null
	>(null);
	const [swapProductionColors, setSwapProductionColors] = useState(false);
	const [confirmMoveModalData, setConfirmMoveModalData] =
		useState<MoveEventData | null>(null);

	const {
		getEventStyle,
		handleDayMouseDown,
		handleDayMouseOver,
		handleDayMouseUp,
	} = useEventHandlers({
		editable,
		hoveredBilletStatusCode,
		selectable,
		currentDate,
		onDateRangeSelect: (startDate, endDate) => {
			if (!selectable) return;
			onDateRangeSelect ? onDateRangeSelect(startDate, endDate) : () => {};
		},
		setIsDragging,
		isDragging,
		setDragStartDate,
		dragStartDate,
		setDragEndDate,
		dragEndDate,
		swapProductionColors,
	});

	const calendarDays = useMemo(() => {
		const monthStart = startOfMonth(currentDate);
		const calendarStart = startOfWeek(monthStart, weekStartsOn);

		const days: Date[] = [];
		let day = calendarStart;
		for (let i = 0; i < 42; i++) {
			const dayToAdd = new Date(day);
			days.push(new Date(day));
			day = addDays(day, 1);
		}

		return days;
	}, [currentDate, weekStartsOn]);

	const weeks = useMemo(() => {
		const weeksArray: Date[][] = [];
		for (let i = 0; i < calendarDays.length; i += 7) {
			weeksArray.push(calendarDays.slice(i, i + 7));
		}

		return weeksArray;
	}, [calendarDays]);

	// Process events into segments
	const eventSegments = useMemo(() => {
		const segments: EventSegment[] = [];
		const serverTz = TimeService.getInstance().getServerTimeZone();

		events.forEach((event) => {
			const eventStart = moment.tz(event.Start, serverTz);
			const eventEnd = moment.tz(event.End!, serverTz);

			weeks.forEach((week, weekIndex) => {
				let firstDayIndex = -1;
				let lastDayIndex = -1;

				week.forEach((day, dayIndex) => {
					const year = day.getFullYear();
					const month = String(day.getMonth() + 1).padStart(2, "0");
					const dayOfMonth = String(day.getDate()).padStart(2, "0");
					const dayStr = `${year}-${month}-${dayOfMonth}`;

					const dayInServerTz = moment.tz(dayStr, serverTz);
					const dayStart = dayInServerTz.clone().startOf("day");
					const dayEnd = dayInServerTz.clone().endOf("day");

					const eventIsInThisDay =
						eventStart.isBefore(dayEnd) && eventEnd.isAfter(dayStart);

					if (eventIsInThisDay) {
						if (firstDayIndex === -1) firstDayIndex = dayIndex;
						lastDayIndex = dayIndex;
					}
				});

				if (firstDayIndex !== -1) {
					const firstDayOfWeek = week[firstDayIndex];
					const lastDayOfWeek = week[lastDayIndex];

					const firstYear = firstDayOfWeek.getFullYear();
					const firstMonth = String(firstDayOfWeek.getMonth() + 1).padStart(
						2,
						"0",
					);
					const firstDay = String(firstDayOfWeek.getDate()).padStart(2, "0");
					const firstDayStr = `${firstYear}-${firstMonth}-${firstDay}`;

					const lastYear = lastDayOfWeek.getFullYear();
					const lastMonth = String(lastDayOfWeek.getMonth() + 1).padStart(
						2,
						"0",
					);
					const lastDay = String(lastDayOfWeek.getDate()).padStart(2, "0");
					const lastDayStr = `${lastYear}-${lastMonth}-${lastDay}`;

					const eventStartsThisDay =
						eventStart.format("YYYY-MM-DD") === firstDayStr;
					const eventEndsThisDay = eventEnd.format("YYYY-MM-DD") === lastDayStr;

					segments.push({
						event,
						dayIndex: firstDayIndex,
						weekIndex,
						isStart: eventStartsThisDay,
						isEnd: eventEndsThisDay,
						segStart: eventStart.toDate(),
						segEnd: eventEnd.toDate(),
						originalStart: eventStart.toDate(),
						originalEnd: eventEnd.toDate(),
						spanDays: lastDayIndex - firstDayIndex + 1,
						firstDayInWeek: firstDayIndex,
						lastDayInWeek: lastDayIndex,
					});
				}
			});
		});

		return segments;
	}, [events, weeks]);

	const segmentsByWeekAndDayWithRows = useMemo(() => {
		const grouped: { segments: EventSegment[]; rows: EventSegment[][] }[][] =
			weeks.map(() =>
				Array(7)
					.fill(null)
					.map(() => ({ segments: [], rows: [] })),
			);

		eventSegments.forEach((segment) => {
			const dayData = grouped[segment.weekIndex][segment.dayIndex];
			dayData.segments.push(segment);
		});

		grouped.forEach((week, weekIndex) => {
			// Collect all segments in this week
			const allWeekSegments: EventSegment[] = [];
			week.forEach((dayData) => {
				dayData.segments.sort(compareEventSegments);
				allWeekSegments.push(...dayData.segments);
			});

			// Remove duplicates (same event appearing in multiple days)
			const uniqueSegments = allWeekSegments.filter(
				(segment, index, self) =>
					index ===
					self.findIndex((s) => s.event.EventId === segment.event.EventId),
			);

			// Organize all week segments into rows
			const weekRows = organizeEventsInRows(uniqueSegments);

			// Create a map of eventId to rowIndex
			const eventToRowMap = new Map<number, number>();
			weekRows.forEach((row, rowIndex) => {
				row.forEach((segment) => {
					eventToRowMap.set(segment.event.EventId, rowIndex);
				});
			});

			// Assign the correct row to each segment based on the week-level organization
			week.forEach((dayData, dayIndex) => {
				const rowsForDay: EventSegment[][] = [];

				dayData.segments.forEach((segment) => {
					const rowIndex = eventToRowMap.get(segment.event.EventId) ?? 0;

					// Ensure the row exists
					while (rowsForDay.length <= rowIndex) {
						rowsForDay.push([]);
					}

					rowsForDay[rowIndex].push(segment);
				});

				dayData.rows = rowsForDay;
			});
		});

		return grouped;
	}, [eventSegments, weeks]);

	const eventsByWeekAndDay = useMemo(() => {
		const covered: Event[][][] = weeks.map(() =>
			Array.from({ length: 7 }, () => [] as Event[]),
		);

		eventSegments.forEach((segment) => {
			const from = segment.firstDayInWeek ?? segment.dayIndex;
			const to = segment.lastDayInWeek ?? segment.dayIndex;

			for (let dayIndex = from; dayIndex <= to; dayIndex++) {
				covered[segment.weekIndex][dayIndex].push(segment.event);
			}
		});

		covered.forEach((week) =>
			week.forEach((dayEvents) =>
				dayEvents.sort((a, b) => a.Start.getTime() - b.Start.getTime()),
			),
		);

		return covered;
	}, [eventSegments, weeks]);

	const dayNames = useMemo(() => {
		const names: string[] = [];
		const baseDate = startOfWeek(new Date(), weekStartsOn);
		for (let i = 0; i < 7; i++) {
			const day = addDays(baseDate, i);
			names.push(
				day.toLocaleDateString("en-US", { weekday: "short" }).toUpperCase(),
			);
		}
		return names;
	}, [weekStartsOn]);

	const {
		handleEventDragStart,
		handleDayMouseOver: updateHoveredDropDate,
		isDraggingEvent,
		draggedEvent,
		dragPosition,
		hoveredDropDate,
		dropLineInfo,
		previewDates,
	} = useEventDragDrop({
		editable: editable,
		onEventDrop: (eventId, newStartDate, newEndDate) => {
			if (!editable) return;
			const draggedSegment = eventSegments.find(
				(seg) => seg.event.EventId === eventId,
			);

			if (!draggedSegment) {
				onEventDrop?.(eventId, newStartDate, newEndDate);
				return;
			}

			const draggedEventData = draggedSegment.event;

			const newStartTime = newStartDate.getTime();
			const newEndTime = newEndDate.getTime();

			// If the new start and end times are the same as the original, no change is needed.
			if (
				newStartTime === draggedSegment.originalStart.getTime() &&
				newEndTime === draggedSegment.originalEnd.getTime()
			) {
				return;
			}

			// Get unique events sorted by start time
			const uniqueEvents = eventSegments
				.filter(
					(seg, index, self) =>
						index ===
							self.findIndex((s) => s.event.EventId === seg.event.EventId) &&
						seg.event.EventId !== eventId,
				)
				.sort((a, b) => a.originalStart.getTime() - b.originalStart.getTime());

			// Event before: the last event that ends before or at the new start
			const eventBeforeSegment =
				uniqueEvents
					.filter((seg) => seg.originalEnd.getTime() <= newStartTime)
					.pop() ?? null;

			// Event after: the first event that starts at or after the new end
			const eventAfterSegment =
				uniqueEvents.find((seg) => seg.originalStart.getTime() >= newEndTime) ??
				null;

			const toProcessedRow = (event: Event): ProcessedRow =>
				({
					...event,
					OriginalStart: event.Start,
					OriginalEnd: event.End ?? undefined,
					IsFirstRow: true,
				}) as ProcessedRow;

			setConfirmMoveModalData({
				draggedEvent: toProcessedRow(draggedEventData),
				originalStart: draggedSegment.originalStart,
				originalEnd: draggedSegment.originalEnd,
				newStart: newStartDate,
				newEnd: newEndDate,
				eventBefore: eventBeforeSegment
					? toProcessedRow(eventBeforeSegment.event)
					: null,
				eventAfter: eventAfterSegment
					? toProcessedRow(eventAfterSegment.event)
					: null,
			});
		},
		eventSegments,
	});

	const handleConfirmMove = () => {
		if (!confirmMoveModalData) return;

		onEventDrop?.(
			confirmMoveModalData.draggedEvent.EventId,
			confirmMoveModalData.newStart,
			confirmMoveModalData.newEnd,
		);

		setConfirmMoveModalData(null);
	};

	const toggleDayExpansion = (date: Date) => {
		const key = getDayKey(date);
		setExpandedDays((prev) => {
			const newSet = new Set(prev);
			if (newSet.has(key)) {
				newSet.delete(key);
			} else {
				newSet.add(key);
			}
			return newSet;
		});
	};

	useEffect(() => {
		setCurrentDate(initialDate);
	}, [initialDate]);

	useEffect(() => {
		const handleMouseUp = () => {
			if (isDragging) {
				handleDayMouseUp();
			}
		};

		if (isDragging) {
			document.addEventListener("mouseup", handleMouseUp);
			return () => {
				document.removeEventListener("mouseup", handleMouseUp);
			};
		}
	}, [isDragging, dragStartDate, dragEndDate]);

	useEffect(() => {
		if (isDraggingEvent) {
			updateHoveredDropDate();
		}
	}, [isDraggingEvent, dragPosition, updateHoveredDropDate]);

	return (
		<Box
			sx={{
				width: "100%",
				height: "100%",
				display: "flex",
				flexDirection: "column",
				overflow: "auto",
				userSelect: "none",
			}}
		>
			{/* Day names header */}
			<Box
				sx={{
					display: "grid",
					gridTemplateColumns: "repeat(7, 1fr)",
					borderBottom: "1px solid #e0e0e0",
					backgroundColor: (theme) => theme.palette.primary.main,
					borderRadius: 1,
				}}
			>
				{dayNames.map((name, index) => (
					<Box
						key={index}
						sx={{
							px: 1,
							py: 0.5,
							textAlign: "center",
							fontSize: "0.675rem",
							fontWeight: 500,
							color: (theme) => theme.palette.primary.contrastText,
							borderRight: index < 6 ? "1px solid #f0f0f0" : "none",
						}}
					>
						{name}
					</Box>
				))}
			</Box>

			{/* Calendar grid */}
			<Box sx={{ flex: 1, overflow: "hidden" }}>
				{weeks.map((week, weekIndex) => (
					<Box
						key={weekIndex}
						sx={{
							display: "grid",
							gridTemplateColumns: "repeat(7, 1fr)",
							minHeight: "120px",
							borderBottom: "1px solid #e0e0e0",
						}}
					>
						{week.map((day, dayIndex) => {
							const isCurrentMonth = isDayInCurrentMonth(day, currentDate);
							const isDayToday = isToday(day);
							const dayData = segmentsByWeekAndDayWithRows[weekIndex][dayIndex];
							const eventRows = dayData.rows;
							const dayEvents = eventsByWeekAndDay[weekIndex][dayIndex];
							const dayKey = getDayKey(day);
							const isExpanded = expandedDays.has(dayKey);

							// Calcular cuántas filas mostrar
							const visibleRows = isExpanded
								? eventRows
								: eventRows.slice(0, eventsLimitPerDay);
							const hiddenCount = eventRows.length - visibleRows.length;

							// Calculate if this day is in the dragged event's new span
							const newDates =
								isDraggingEvent && draggedEvent && hoveredDropDate
									? calculateNewEventDates(
											draggedEvent.segment.originalStart,
											draggedEvent.segment.originalEnd,
											hoveredDropDate,
										)
									: null;

							// Check if current day is within the event span by iterating through days
							const isInDraggedEventSpan = (() => {
								if (!newDates) return false;

								const serverTz = TimeService.getInstance().getServerTimeZone();
								const currentDayStr = day.toISOString().split("T")[0];
								let current = moment
									.tz(newDates.start, serverTz)
									.startOf("day");
								const end = moment.tz(newDates.end, serverTz).startOf("day");

								while (current.isBefore(end) || current.isSame(end, "day")) {
									if (current.format("YYYY-MM-DD") === currentDayStr) {
										return true;
									}
									current.add(1, "day");
								}
								return false;
							})();

							const isHoveredDropZone =
								hoveredDropDate &&
								hoveredDropDate.toDateString() === day.toDateString();

							return (
								<Box
									key={dayIndex}
									onMouseDown={(e) => {
										if (e.button === 0) {
											handleDayMouseDown(day);
										}
									}}
									onMouseOver={() => handleDayMouseOver(day)}
									onMouseUp={handleDayMouseUp}
									onContextMenu={(e) => {
										e.preventDefault();
										showContextMenu(
											e,
											{
												isEmptySpace: true,
												clickedDate: day,
												fromCalendar: true,
												dayEvents,
											},
											"calendarContext",
										);
									}}
									data-date={day.toISOString().split("T")[0]}
									sx={{
										borderRight: dayIndex < 6 ? "1px solid #e0e0e0" : "none",
										py: 1,
										backgroundColor:
											(isInDraggedEventSpan || isHoveredDropZone) &&
											isDraggingEvent
												? "#fff3cd"
												: isCurrentMonth &&
														isDateInDragRange(day, dragStartDate, dragEndDate)
													? "#e3f2fd"
													: isCurrentMonth
														? "white"
														: "#f7f7f7ff",
										borderTop:
											(isInDraggedEventSpan || isHoveredDropZone) &&
											isDraggingEvent
												? "3px solid #ffc107"
												: "none",
										cursor:
											selectable && isCurrentMonth ? "pointer" : "default",
										transition:
											"background-color 0.2s ease, border-top 0.2s ease",
										position: "relative",
										overflow: "visible",
										"&:hover": {
											backgroundColor:
												selectable && isDragging ? "#e3f2fd" : "#f5f5f5",
										},
									}}
								>
									{/* Day number */}
									<Box
										sx={{
											display: "flex",
											justifyContent: "flex-end",
											marginBottom: 0.5,
										}}
									>
										<Typography
											sx={{
												fontSize: "0.875rem",
												fontWeight: 500,
												width: 28,
												height: 28,
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
												borderRadius: "50%",
												backgroundColor: (theme) =>
													isDayToday
														? theme?.palette?.primary?.main || "#1976d2"
														: "transparent",
												color: isDayToday
													? "#fff"
													: !isCurrentMonth
														? "grey"
														: "#inherit",
											}}
										>
											{day.getDate()}
										</Typography>
									</Box>

									{/* Events organized by files */}
									<Box
										sx={{
											position: "relative",
											minHeight: `${
												visibleRows.length * 26 + (hiddenCount > 0 ? 20 : 0)
											}px`,
											overflow: "visible",
										}}
									>
										{visibleRows.map((row, rowIndex) => {
											return row.map((segment) => {
												const isFirstDay =
													(segment.firstDayInWeek ?? segment.dayIndex) ===
													dayIndex;

												if (!isFirstDay) return null;

												const span = segment.spanDays || 1;
												const segIndex = `${segment.event.EventId}-${rowIndex}-${dayIndex}`;

												const eventStyles = getEventStyle(segment);
												const eventType = eventTypes.find(
													(t) => t.code === segment.event?.EventTypeCode,
												);

												const isCompleted = segment.event.StatusCode === "C";
												const isConfirmed = segment.event.StatusCode === "K";

												// Calculate the actual width percentage for the event
												const eventWidthPercent =
													span > 1
														? (() => {
																const serverTz =
																	TimeService.getInstance().getServerTimeZone();
																const momentStart = moment.tz(
																	segment.segStart,
																	serverTz,
																);
																const momentEnd = moment.tz(
																	segment.segEnd,
																	serverTz,
																);

																let totalWidth = 0;

																if (segment.isStart) {
																	const startPercent =
																		((momentStart.hour() * 60 +
																			momentStart.minute()) /
																			(24 * 60)) *
																		100;
																	totalWidth += 100 - startPercent;
																} else {
																	totalWidth += 100;
																}

																totalWidth += (span - 2) * 100;

																if (segment.isEnd) {
																	const endPercent =
																		((momentEnd.hour() * 60 +
																			momentEnd.minute()) /
																			(24 * 60)) *
																		100;
																	totalWidth += endPercent;
																} else {
																	totalWidth += 100;
																}

																return totalWidth;
															})()
														: (() => {
																const serverTz =
																	TimeService.getInstance().getServerTimeZone();
																const momentStart = moment.tz(
																	segment.segStart,
																	serverTz,
																);
																const momentEnd = moment.tz(
																	segment.segEnd,
																	serverTz,
																);
																const startPercent =
																	((momentStart.hour() * 60 +
																		momentStart.minute()) /
																		(24 * 60)) *
																	100;
																const endPercent =
																	((momentEnd.hour() * 60 +
																		momentEnd.minute()) /
																		(24 * 60)) *
																	100;
																return endPercent - startPercent;
															})();

												const isNarrowEvent = eventWidthPercent < 50;

												return (
													<Tooltip
														key={segIndex}
														title={
															<Box>
																<Typography
																	variant="body2"
																	sx={{ fontWeight: 600 }}
																>
																	{segment.event.EventTypeCode === "P"
																		? `Section: ${segment.event.SectionName}`
																		: eventType?.name}
																</Typography>
																{segment.event.EventTypeCode === "P" ? (
																	<Box display="flex" gap={0.5}>
																		<Typography variant="caption">
																			{segment.event.StatusName}
																		</Typography>
																		<Typography variant="caption">
																			{segment.event?.ActualRolledTons
																				? `• ${segment.event?.ActualRolledTons.toFixed(
																						0,
																					)} tons`
																				: ""}
																		</Typography>
																	</Box>
																) : (
																	<Box display="flex" gap={0.5}>
																		<Typography variant="caption">
																			{segment.event.EventComments}
																		</Typography>
																	</Box>
																)}
																<Typography variant="caption">
																	{dxtToLocalServerTime(
																		segment.originalStart,
																		"MM/dd HH:mm",
																	)}{" "}
																	-{" "}
																	{dxtToLocalServerTime(
																		segment.originalEnd,
																		"MM/dd HH:mm",
																	)}
																</Typography>
																<Typography variant="caption" display="block">
																	Duration:{" "}
																	{moment
																		.duration(
																			segment.originalEnd.getTime() -
																				segment.originalStart.getTime(),
																			"milliseconds",
																		)
																		.format(
																			`w [weeks], d [days], h [hours], m [minutes]`,
																			{
																				trim: "both",
																			},
																		)}
																</Typography>
															</Box>
														}
														open={isDraggingEvent ? false : undefined}
													>
														<Box
															data-event-id={segment.event.EventId}
															onClick={() => {
																onEventClick(segment.event ?? null);
															}}
															onMouseDown={(e) => {
																if (editable && e.button === 0) {
																	e.stopPropagation();
																	handleEventDragStart(e as any, segment);
																}
															}}
															onContextMenu={(e) => {
																e.preventDefault();
																if (segment.event.EventId) {
																	showContextMenu(
																		e,
																		{ ...segment.event, fromCalendar: true },
																		"calendarContext",
																	);
																}
															}}
															sx={{
																position: "absolute",
																top: `${rowIndex * 26}px`,
																left: 0,
																zIndex: 1,
																cursor: editable ? "move" : "pointer",
																borderRadius: "4px",
																padding: "2px 6px",
																fontSize: "0.75rem",
																whiteSpace: "nowrap",
																textOverflow: "ellipsis",
																overflow: "visible",
																backgroundColor: eventStyles.backgroundColor,
																opacity:
																	isDraggingEvent &&
																	draggedEvent?.segment.event.EventId ===
																		segment.event.EventId
																		? 0.5
																		: 1,
																borderLeft: segment.isStart
																	? isNarrowEvent
																		? getNarrowBorderLeft(
																				eventStyles.borderLeft,
																			)
																		: eventStyles.borderLeft
																	: "none",
																color: eventStyles.color,
																height: "25px",
																width:
																	span > 1
																		? `calc(${eventWidthPercent}% + ${
																				span - 1
																			}px)`
																		: eventStyles.width,
																marginLeft: eventStyles.marginLeft,
																pointerEvents: isDraggingEvent
																	? "none"
																	: "auto",

																"&:hover": {
																	opacity: 0.9,
																	transform: "translateY(-1px)",
																	boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
																	zIndex: 1,
																	overflow: "visible",
																},
																transition: "all 0.2s ease",
																clipPath: "inset(0 0 0 -10px)",
															}}
														>
															{segment.isStart &&
																isCompleted &&
																!isNarrowEvent && (
																	<Done
																		sx={{
																			position: "absolute",
																			left: "-22px",
																			top: "50%",
																			transform: "translateY(-50%)",
																			fontSize: "16px",
																			color: "#065F46",
																			zIndex: 999,
																		}}
																	/>
																)}

															{segment.isStart &&
																isConfirmed &&
																!isNarrowEvent && (
																	<DoneAll
																		sx={{
																			position: "absolute",
																			left: "-22px",
																			top: "50%",
																			transform: "translateY(-50%)",
																			fontSize: "16px",
																			color: "#065F46",
																			zIndex: 999,
																		}}
																	/>
																)}

															<>
																{segment.event.EventTypeCode === "P"
																	? segment.event?.SectionName
																		? `${segment.event?.SectionName}`
																		: ""
																	: segment.event.EventTypeCode !== "P"
																		? `${eventType?.name}`
																		: ""}
																{eventType?.code === "P"
																	? ` • ${
																			productionTypes.find(
																				(t) =>
																					t.code === segment.event?.StatusCode,
																			)?.name ?? ""
																		}${`${
																			segment.event?.ActualRolledTons
																				? ` • ${segment.event?.ActualRolledTons.toFixed(
																						0,
																					)} tons`
																				: ""
																		}`}`
																	: `${segment.event.EventComments ? ` • ${segment.event.EventComments}` : ""}`}
															</>
														</Box>
													</Tooltip>
												);
											});
										})}

										{/* Show more button */}
										{hiddenCount > 0 && (
											<Button
												size="small"
												onClick={() => {
													toggleDayExpansion(day);
												}}
												onMouseDown={(e) => e.stopPropagation()}
												sx={{
													position: "absolute",
													top: `${visibleRows.length * 26}px`,
													left: 0,
													fontSize: "0.7rem",
													textTransform: "none",
													padding: "2px 6px",
													minWidth: "auto",
													color: (theme) =>
														theme?.palette?.primary?.main || "#1976d2",
													zIndex: 1,
												}}
											>
												{eventLimitText.replace(
													"{count}",
													hiddenCount.toString(),
												)}
											</Button>
										)}
									</Box>
								</Box>
							);
						})}
					</Box>
				))}
			</Box>

			{isDraggingEvent &&
				draggedEvent &&
				dragPosition &&
				(() => {
					const style = getEventStyle(draggedEvent.segment);
					const segment = draggedEvent.segment;
					const event = segment.event;

					return (
						<Box
							sx={{
								position: "fixed",
								pointerEvents: "none",
								left: `${dragPosition.x + 15}px`,
								top: `${dragPosition.y + 20}px`,
								width: "300px",
								backgroundColor: style.backgroundColor,
								color: style.color,
								padding: "8px 12px",
								borderRadius: "6px",
								fontSize: "0.8rem",
								boxShadow: "0 6px 20px rgba(0, 0, 0, 0.35)",
								zIndex: 10000,
								border: `2px solid ${style.borderLeft || "#333"}`,
								opacity: 0.98,
								fontWeight: 600,
								cursor: "grabbing",
								display: "flex",
								flexDirection: "column",
								gap: "4px",
							}}
						>
							{/* Original Time Range */}
							<Box
								sx={{
									fontSize: "0.7rem",
									fontWeight: 500,
									letterSpacing: "0.5px",
									opacity: 0.75,
									textDecoration: "line-through",
								}}
							>
								Old:{" "}
								{formatTimeRange(segment.originalStart, segment.originalEnd)}
							</Box>

							{/* New Time Range */}
							{previewDates && (
								<Box
									sx={{
										fontSize: "0.7rem",
										fontWeight: 700,
										letterSpacing: "0.5px",
										opacity: 0.95,
										color: "inherit",
									}}
								>
									New: {formatTimeRange(previewDates.start, previewDates.end)}
								</Box>
							)}

							{/* Event Details */}
							<Box
								sx={{
									fontSize: "0.85rem",
									fontWeight: 600,
									whiteSpace: "nowrap",
									textOverflow: "ellipsis",
									overflow: "hidden",
								}}
							>
								{event.EventTypeCode === "P"
									? event?.SectionName
										? `Section: ${event?.SectionName}`
										: ""
									: eventTypes.find((t) => t.code === event?.EventTypeCode)
											?.name}
							</Box>

							{/* Status */}
							{event.EventTypeCode === "P" && event?.StatusCode && (
								<Box
									sx={{
										fontSize: "0.7rem",
										opacity: 0.8,
										fontWeight: 500,
									}}
								>
									{
										productionTypes.find((t) => t.code === event?.StatusCode)
											?.name
									}{" "}
									{" - "}
									{event.StatusCode === "P"
										? event?.ScheduledRolledTons
										: event?.ActualRolledTons}{" "}
									{" tons"}
								</Box>
							)}
						</Box>
					);
				})()}

			{/* Drop Position Indicator */}
			{isDraggingEvent && dropLineInfo && (
				<>
					{/* Line indicator */}
					<Box
						sx={{
							position: "fixed",
							left: `${dropLineInfo.x}px`,
							top: `${dropLineInfo.y + 12}px`,
							width: "3px",
							height: `${dropLineInfo.height - 12}px`,
							backgroundColor: (theme) =>
								dropLineInfo.position === "empty"
									? theme.palette.primary.main
									: dropLineInfo.position === "before"
										? theme.palette.success.main
										: theme.palette.info.main,
							zIndex: 9999,
							pointerEvents: "none",
							boxShadow: "0 0 4px rgba(0,0,0,0.3)",
							transition: "all 0.1s ease",
						}}
					/>

					{/* Circle indicator */}
					<Box
						sx={{
							position: "fixed",
							left: `${dropLineInfo.x - 6}px`,
							top: `${dropLineInfo.y}px`,
							width: "14px",
							height: "14px",
							borderRadius: "50%",
							backgroundColor: (theme) =>
								dropLineInfo.position === "empty"
									? theme.palette.primary.main
									: dropLineInfo.position === "before"
										? theme.palette.success.main
										: theme.palette.info.main,
							border: "2px solid white",
							zIndex: 10000,
							pointerEvents: "none",
							boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
							transition: "all 0.1s ease",
						}}
					/>

					{/* Circle indicator */}
					<Box
						sx={{
							position: "fixed",
							left: `${dropLineInfo.x - 6}px`,
							top: `${dropLineInfo.y + dropLineInfo.height - 14}px`,
							width: "14px",
							height: "14px",
							borderRadius: "50%",
							backgroundColor: (theme) =>
								dropLineInfo.position === "empty"
									? theme.palette.primary.main
									: dropLineInfo.position === "before"
										? theme.palette.success.main
										: theme.palette.info.main,
							border: "2px solid white",
							zIndex: 10000,
							pointerEvents: "none",
							boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
							transition: "all 0.1s ease",
						}}
					/>
				</>
			)}

			{/* Billet Status Legend */}
			<Box
				style={{
					display: "flex",
					gap: 10,
					padding: 2,
					flexWrap: "wrap",
					justifyContent: "space-between",
					alignItems: "center",
				}}
			>
				<Box style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
					{billetStatus.map((type) => (
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								gap: (theme) => theme.spacing(1),
								cursor: "pointer",
								px: 1,
								py: 0.5,
								borderRadius: 1,
								backgroundColor:
									hoveredBilletStatusCode === type.code
										? "#e0e0e0"
										: "transparent",
								transition: "background-color 0.2s",
							}}
							key={type.code}
							onMouseEnter={() => setHoveredBilletStatusCode(type.code)}
							onMouseLeave={() => setHoveredBilletStatusCode(null)}
						>
							<Box
								sx={{
									width: 15,
									height: 15,
									borderRadius: 4,
									backgroundColor: type.backgroundColor,
								}}
							/>
							<Typography variant="body2" color="textSecondary">
								Billets {type.name}
								{` (${countSectionsByStatus(eventSegments, type.code)})`}
							</Typography>
						</Box>
					))}
				</Box>
				<FormControlLabel
					control={
						<Checkbox
							checked={swapProductionColors}
							onChange={(e) => setSwapProductionColors(e.target.checked)}
						/>
					}
					label="Swap Production Colors"
				/>
			</Box>

			<DragModeSnackbar visible={isDraggingEvent} />

			<ConfirmMoveEventModal
				open={confirmMoveModalData !== null}
				moveData={confirmMoveModalData}
				onConfirm={handleConfirmMove}
				onCancel={() => setConfirmMoveModalData(null)}
			/>
		</Box>
	);
};
